import React from 'react';

import { Text, TextType } from 'ui-components/components/text';

import { EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { useStoreUpdateContext } from '../../context';
import BrandedReturns from '../branded-returns';
import { EmailSettingsSection } from '../email-settings-section';
import { useReturnsTabContext } from '../hooks/use-returns-tab-context';
import useTrackReturnsSettings from '../hooks/use-track-returns-settings';
import { ReturnPolicySection } from '../return-policy-section';
import { ReturnsTabExpandedSection } from '../returns-tab-expanded-section';
import { ReturnsTabHeader } from '../returns-tab-header';
import { ReturnsTabLearnMoreLink } from '../returns-tab-learn-more-link';
import { ReturnsTabSection } from '../returns-tab-section';
import { ActionButton } from './action-button';
import messages from './messages';
import style from './style.module.less';

/** @typedef {import("../types").ReturnSettings} ReturnSettings */

/**
 * Standard Returns Section component
 * Displays the Standard Returns section in the Returns tab
 * @typedef {object} StandardReturnsSectionProps
 * @property {object} returnSettings - The return settings for the store
 * @param {StandardReturnsSectionProps} props - Component props
 * @returns {React.ReactElement|null} The Standard Returns section or null if it shouldn't be displayed
 */
export const StandardReturnsPanel = ({ returnSettings }) => {
  const {
    hasLegacyBrandedReturns,
    isShipstationReturnsEnabled,
    showStandardReturnPortalConfigForEnhancedStore,
    supportsEnhancedReturns,
    useBrandedReturns,
  } = useReturnsTabContext();

  const trackReturns = useTrackReturnsSettings(EventSourceStrings.STORE_SETTINGS_RETURNS_TAB);
  const { updateStoreSetting } = useStoreUpdateContext();
  const GROUP_NAME = 'returns';
  const { returnPolicyMessage, emailMessage } = returnSettings;
  const hideStandardReturnsPanel =
    (supportsEnhancedReturns && !showStandardReturnPortalConfigForEnhancedStore) || isShipstationReturnsEnabled;

  // Keeping this function commented as it may be needed in the future
  const toggleBrandedReturns = () => {
    const newState = !useBrandedReturns;
    trackReturns.trackActivateStore(newState);
    updateStoreSetting(GROUP_NAME, 'useBrandedReturns', newState);
  };

  if (hideStandardReturnsPanel) return null;

  return (
    <ReturnsTabSection>
      <ReturnsTabHeader>
        <div>
          <div className={style.returnsTabTitle}>
            <Text className={style.title} textType={TextType.H3} value={messages.standardReturnsTitle} />
          </div>
          <Text textType={TextType.Paragraph} value={messages.standardReturnsDescription} />
          <ReturnsTabLearnMoreLink />
        </div>

        <ActionButton onClick={() => toggleBrandedReturns()} />
      </ReturnsTabHeader>

      {useBrandedReturns ? (
        <ReturnsTabExpandedSection>
          <BrandedReturns returnSettings={returnSettings} trackReturns={trackReturns} />
        </ReturnsTabExpandedSection>
      ) : null}

      {hasLegacyBrandedReturns ? (
        <ReturnsTabExpandedSection>
          <div className={style.messageEditingSections}>
            <ReturnPolicySection returnPolicyMessage={returnPolicyMessage} />
            <EmailSettingsSection className={style.emailSettingsSection} emailMessage={emailMessage} />
          </div>
        </ReturnsTabExpandedSection>
      ) : null}
    </ReturnsTabSection>
  );
};
